import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

// A slow Holt-style artillery tractor: two long caterpillar tracks with
// visible roadwheels and end sprockets, a boxy olive engine hood with an
// exhaust stack, an exposed driver's seat behind the hood, and a tow hook on
// a rear plate for the gun. Front is +z.

const { box, cyl } = styleV3;

const OLIVE = "#5f6b3c";
const KHAKI = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TYRE = "#33342f";

// One caterpillar track at x: a long dark box resting on the ground, with
// roadwheel and sprocket cylinders poking through its sides.
function track(x: number): Part[] {
  const out: Part[] = [
    box(0.15, 0.2, 1.3, TYRE, [x, 0.1, -0.05]),
  ];
  for (const z of [-0.45, -0.15, 0.15]) {
    out.push(cyl(0.06, 0.06, 0.17, 8, GUNMETAL, [x, 0.07, z], [0, 0, Math.PI / 2]));
  }
  out.push(cyl(0.09, 0.09, 0.17, 8, GUNMETAL, [x, 0.11, 0.52], [0, 0, Math.PI / 2]));
  out.push(cyl(0.09, 0.09, 0.17, 8, GUNMETAL, [x, 0.11, -0.62], [0, 0, Math.PI / 2]));
  return out;
}

function parts(): Part[] {
  const out: Part[] = [
    ...track(-0.22),
    ...track(0.22),

    // hull deck spanning the tracks
    box(0.36, 0.1, 1.2, OLIVE, [0, 0.25, -0.05]),

    // -- Boxy engine hood, radiator and exhaust stack --------------------------
    box(0.3, 0.24, 0.55, OLIVE, [0, 0.42, 0.22]),
    box(0.32, 0.22, 0.04, GUNMETAL, [0, 0.4, 0.51]),
    cyl(0.025, 0.025, 0.22, 6, GUNMETAL, [0.08, 0.62, 0.32]),

    // -- Exposed driver's seat behind the hood ---------------------------------
    cyl(0.03, 0.03, 0.08, 6, GUNMETAL, [0, 0.33, -0.3]),
    box(0.18, 0.04, 0.15, KHAKI, [0, 0.38, -0.3]),
    box(0.18, 0.13, 0.03, KHAKI, [0, 0.46, -0.38]),
    // steering wheel off the back of the hood
    cyl(0.012, 0.012, 0.12, 6, GUNMETAL, [0, 0.44, -0.12], [1.0, 0, 0]),
    cyl(0.05, 0.05, 0.016, 10, TYRE, [0, 0.49, -0.16], [1.0, 0, 0]),

    // -- Tow hook on a rear plate ----------------------------------------------
    box(0.18, 0.1, 0.04, GUNMETAL, [0, 0.2, -0.67]),
    cyl(0.015, 0.015, 0.09, 6, GUNMETAL, [0, 0.17, -0.72]),
    {
      geometry: new THREE.IcosahedronGeometry(0.024, 0),
      color: GUNMETAL,
      position: [0, 0.22, -0.72],
    },
  ];
  return out;
}

export const artilleryTractor = defineModel({
  id: "artillery-tractor",
  name: "Artillery Tractor",
  batchId: "wwi",
  tags: ["vehicle", "wwi", "military", "tractor", "tracked", "artillery", "tow"],
  summary: "A slow Holt-style tracked tractor with an exposed seat and tow hook.",
  description:
    "A WWI Holt-style artillery tractor: long caterpillar tracks with visible " +
    "roadwheels and sprockets, a boxy olive engine hood with an exhaust " +
    "stack, an exposed driver's seat and a rear tow hook for hauling guns.",
  author: "Fable 5",
  footprint: 0.7,
  tiles: { w: 1, h: 2 },
  parts,
  animations: [],
});
